// "Needs attention" — one My Desk panel type (`needsAttention`, N-090).
// Extracted verbatim from Home.tsx (N-089): tasks that are overdue, sent
// back, or blocked, one row per DEFINITION (not per clinic — the same
// standing item planted at all four clinics used to list four times;
// clinics still needing a look are named on the one line instead).
// Owner, 2026-09-10: "those expiring in 90 days shoud go on items need
// attention on managers MY DEsk as well as mine and Candaces" — licenses
// expired or expiring within LICENSE_ATTENTION_DAYS join this same list,
// scoped by visibleClinics() exactly like the tasks (a manager sees their
// clinic's, owner/director see every clinic's). Rows already moved to Old
// Licenses never appear.
import { currentTaskList } from '../lib/bootstrap';
import type { JSX } from 'preact';
import { state, useResource } from '../lib/store';
import { session } from '../lib/session';
import { LOCATION_ORDER, LOCATION_NAMES, type LocationCode } from '../lib/locations';
import { clinicToday } from '../lib/clinicDay';
import { isoToMd } from '../lib/serviceCompliance';
import {
  LICENSES_KEY, fetchLicenses, licensesNeedingAttention, daysUntilExpiry,
  LICENSE_ATTENTION_DAYS, type LicensesData,
} from '../lib/licenses';
import {
  LEASES_KEY, fetchLeases, leasesNeedingAttention, leaseMonthsLeft,
  LEASE_ATTENTION_MONTHS, LEASE_WARN_MONTHS, type LeasesData,
} from '../lib/leases';
import {
  INSURANCE_KEY, fetchInsurance, policiesNeedingAttention, policyDaysLeft, policyLocationNames,
  INSURANCE_ATTENTION_DAYS, INSURANCE_WARN_DAYS, type InsuranceData,
} from '../lib/insurance';
import {
  COMMUNICATIONS_KEY, fetchCommunications, type CommunicationsData,
} from '../lib/communications';
import { daysBetweenIso } from '../lib/civilDate';
import { visibleClinics } from './ClinicTabs';
import { displayStatus, STATUS_LABEL, type TaskRow } from '../pages/Tasks';
import { DeskPanel } from './DeskPanel';

function expiryTiming(days: number | null): string {
  if (days == null) return '';
  if (days < 0) return `overdue ${-days} day${-days === 1 ? '' : 's'}`;
  if (days === 0) return 'expires today';
  return `in ${days} days`;
}

/** Statuses that mean "someone still has to do something about this". */
const NEEDS_ATTENTION = new Set(['OVERDUE', 'SENT_BACK', 'BLOCKED']);

/** OWNER/DIRECTOR, not viewing-as — the same gate leasesRoleGate enforces
 * server-side (src/leases.js) and CompanyLeases.tsx's own canWriteLeases
 * checks client-side. A manager, and an owner wearing the manager lens,
 * never see a lease line here. */
function canSeeLeases(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR'));
}

/** Same gate as canSeeLeases, mirrored for insurance (insuranceRoleGate,
 * src/insurance.js) — a manager never sees a policy's premium, broker or
 * limits here either, not even for their own clinic. */
function canSeeInsurance(): boolean {
  const me = session.value.me;
  return Boolean(me && !me.viewingAs && (me.role === 'OWNER' || me.role === 'DIRECTOR'));
}

export function NeedsAttentionPanel(
  { chromeless = false }: { chromeless?: boolean } = {},
): JSX.Element | null {
  const now = new Date();
  // Hook first (rules of hooks), before any early return below — same
  // discipline as ComplianceAlerts.tsx. The lease fetch is skipped (key
  // null) for anyone but OWNER/DIRECTOR, the same `key ? KEY : null` idiom
  // IntakePanel.tsx and PayHoursDeskCard.tsx already use for a role-gated
  // resource, so a manager's My Desk never even asks the (403-refusing)
  // server for it.
  const licenseLoad = useResource<LicensesData>(LICENSES_KEY, fetchLicenses);
  const seesLeases = canSeeLeases();
  const leaseLoad = useResource<LeasesData>(seesLeases ? LEASES_KEY : null, fetchLeases);
  const seesInsurance = canSeeInsurance();
  const insuranceLoad = useResource<InsuranceData>(seesInsurance ? INSURANCE_KEY : null, fetchInsurance);
  // Owner, 2026-09-27: "put the attached in communications with a must
  // read for all managers and Candace." Every mapped role sees this line —
  // no role gate, same as the server scopes /api/v1/communications and the
  // Communications panel itself.
  const communicationsLoad = useResource<CommunicationsData>(COMMUNICATIONS_KEY, fetchCommunications);
  const tasks = currentTaskList<TaskRow>();
  const workItems = Object.keys(state.value.workItem ?? {});
  const clinics = visibleClinics();
  const today = clinicToday(now);
  const licenseRows = licenseLoad.status === 'ready'
    ? licensesNeedingAttention(licenseLoad.data.rows ?? [], new Set(clinics), today)
    : [];
  const leaseRows = seesLeases && leaseLoad.status === 'ready'
    ? leasesNeedingAttention(leaseLoad.data.leases ?? [], today)
    : [];
  const insuranceRows = seesInsurance && insuranceLoad.status === 'ready'
    ? policiesNeedingAttention(insuranceLoad.data.policies ?? [], today)
    : [];
  const mustReadRows = communicationsLoad.status === 'ready'
    ? communicationsLoad.data.communications.filter((c) => c.mustRead && c.unread)
    : [];
  if (
    !tasks.length && !workItems.length && !licenseRows.length && !leaseRows.length && !insuranceRows.length
    && !mustReadRows.length
  ) return null;

  const attentionRows: {
    key: string; code: string; title: string; detail: string; task: boolean; warn: boolean; href?: string;
  }[] = [];
  const seenAttention = new Set<string>();
  for (const code of LOCATION_ORDER.filter((c) => clinics.includes(c))) {
    for (const t of tasks) {
      if (t.location_code !== code || !NEEDS_ATTENTION.has(displayStatus(t, now))) continue;
      const nameKey = (t.definition_name || '').trim().toLowerCase();
      if (seenAttention.has(nameKey)) continue;
      seenAttention.add(nameKey);
      const status = displayStatus(t, now);
      const where = LOCATION_ORDER
        .filter((c) => clinics.includes(c))
        .filter((c) => tasks.some((x) =>
          (x.definition_name || '').trim().toLowerCase() === nameKey
          && x.location_code === c
          && NEEDS_ATTENTION.has(displayStatus(x, now))))
        .map((c) => LOCATION_NAMES[c] ?? c);
      attentionRows.push({
        key: `def-${t.task_definition_id}`,
        code: where.length === 1 ? code : `${where.length} clinics`,
        title: t.definition_name,
        detail: `${STATUS_LABEL[status]} · due ${t.due_rule}${where.length > 1 ? ` · ${where.join(', ')}` : ''}`,
        task: true,
        warn: false,
      });
    }
  }
  for (const r of licenseRows) {
    attentionRows.push({
      key: `license-${r.id}`,
      code: LOCATION_NAMES[r.locationCode as LocationCode] ?? r.locationCode,
      title: 'License expiring',
      detail: `${r.kind}${r.issuer ? ` · ${r.issuer}` : ''} · expires ${isoToMd(r.expiresOn)} · ${expiryTiming(daysUntilExpiry(r, today))}`,
      task: false,
      warn: false,
    });
  }
  // Owner, 2026-09-26: "i need alert 12 month in advance and 6 months in
  // advance on all these lease expirations" — one line per lease within
  // LEASE_ATTENTION_MONTHS, warn-coloured once it is inside LEASE_WARN_MONTHS
  // (the same distinction src/lease_alerts.js's own two alerts draw, in
  // months here rather than a day count).
  for (const l of leaseRows) {
    const months = leaseMonthsLeft(l.expirationDate as string, today);
    attentionRows.push({
      key: `lease-${l.id}`,
      code: LOCATION_NAMES[l.locationCode as LocationCode] ?? l.locationCode,
      title: `Lease ends ${isoToMd(l.expirationDate)}`,
      detail: `— ${months} month${months === 1 ? '' : 's'}`,
      task: false,
      warn: months <= LEASE_WARN_MONTHS,
    });
  }
  // Owner, 2026-09-26: same ask as the lease alerts above, same evening —
  // one line per policy within INSURANCE_ATTENTION_DAYS, warn-coloured once
  // it is inside INSURANCE_WARN_DAYS (this build's own 60/30-day judgment
  // call — src/insurance_alerts.js says why days, not months, here).
  for (const p of insuranceRows) {
    const days = policyDaysLeft(p.expirationDate as string, today);
    const names = policyLocationNames(p);
    attentionRows.push({
      key: `insurance-${p.id}`,
      code: names.length === 0 ? 'Company' : names.length === 1 ? names[0] : `${names.length} clinics`,
      title: `${p.coverageType} expires ${isoToMd(p.expirationDate)}`,
      detail: `— ${days} day${days === 1 ? '' : 's'}`,
      task: false,
      warn: days <= INSURANCE_WARN_DAYS,
    });
  }
  // Owner, 2026-09-27: "put the attached in communications with a must
  // read for all managers and Candace … something we track." One line per
  // must-read communication the VIEWER has not yet read — a row they have
  // already read drops off this list even though mustRead stays true on
  // the row itself (CompanyCommunications.tsx's own "Company page addresses
  // a communication" hash, comm-<id>, reused rather than a new route).
  for (const c of mustReadRows) {
    attentionRows.push({
      key: `communication-${c.id}`,
      code: 'Company',
      title: `Must read: ${c.subject || '(no subject)'}`,
      detail: c.fromName || c.fromAddress ? `— ${c.fromName || c.fromAddress}` : '',
      task: false,
      warn: true,
      href: `/company#comm-${c.id}`,
    });
  }
  return (
    <DeskPanel
      title="Needs attention"
      subtitle={`Tasks that are overdue, sent back, or blocked; licenses expiring within ${LICENSE_ATTENTION_DAYS} days; leases expiring within ${LEASE_ATTENTION_MONTHS} months; insurance policies expiring within ${INSURANCE_ATTENTION_DAYS} days; and must-read communications.`}
      testId="needs-attention-panel"
      chromeless={chromeless}
    >
      {/* Owner, 2026-10-12: rows read like My Mail — the title bold, the
         detail muted underneath, where it is (clinic, "Company", "2
         clinics") small on the right, instead of a wide left label. */}
      <div class="desk-panel-scroll" data-testid="needs-attention">
        {attentionRows.length === 0 ? (
          <p class="empty">Nothing flagged — every task is on track.</p>
        ) : (
          <ul class="desk-list">
            {attentionRows.map((item) => {
              const body = (
                <>
                  <span class="desk-row-line">
                    <span class="desk-row-main">{item.title}</span>
                    {/* One clinic (a manager): it is their clinic — no name (owner, 2026-10-12). */}
                    {clinics.length > 1 ? <span class="desk-row-value">{item.code}</span> : null}
                  </span>
                  {item.detail ? (
                    <span class={item.warn ? 'desk-row-sub warn' : 'desk-row-sub'}>
                      {item.detail.replace(/^— /, '')}
                    </span>
                  ) : null}
                </>
              );
              return (
                <li key={item.key} data-testid={`needs-attention-${item.key}`}>
                  {item.href ? <a class="desk-row" href={item.href}>{body}</a> : <div class="desk-row">{body}</div>}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </DeskPanel>
  );
}
